import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Award, Briefcase, Target, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/shared/Common";
import { api } from "@/lib/api";
import { OPP_STATUS, clp } from "@/lib/format";
import { Pick, useLookups } from "./Sales";

const Kpi = ({ icon: Icon, label, value, to, tone, testid }) => (
  <Link to={to} data-testid={testid} className="syc-card p-5 hover:-translate-y-0.5 transition-transform block">
    <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5b6656]">{label}</span>
      <Icon size={18} className={tone === "red" ? "text-[#b42318]" : "text-[#258F00]"} /></div>
    <div className="mt-3 text-2xl font-extrabold tabular-nums">{value}</div>
  </Link>
);

const Filters = ({ f, setF, lookups, ccs }) => (
  <div className="syc-card p-4 mb-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="gerencia-filters">
    <Input data-testid="filter-date-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
    <Input data-testid="filter-date-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
    <Pick testid="filter-client" value={f.client_id} onChange={(v) => setF({ ...f, client_id: v })} options={lookups.clients} placeholder="Cliente" />
    <Pick testid="filter-cc" value={f.cost_center_id} onChange={(v) => setF({ ...f, cost_center_id: v })} options={ccs} placeholder="Centro de costo" />
    <Pick testid="filter-owner" value={f.owner_id} onChange={(v) => setF({ ...f, owner_id: v })} options={lookups.users} placeholder="Responsable" />
    <Button data-testid="filter-clear-btn" variant="outline" onClick={() => setF({ date_from: "", date_to: "", client_id: "", cost_center_id: "", owner_id: "" })}>Limpiar</Button>
  </div>
);

export default function GerenciaDashboard() {
  const lookups = useLookups();
  const [ccs, setCcs] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", client_id: "", cost_center_id: "", owner_id: "" });
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/gerencia/cost-centers").then((r) => setCcs(r.data.map((c) => ({ id: c.id, name: `${c.code} · ${c.name}` })))).catch(() => {}); }, []);
  useEffect(() => { api.get("/gerencia/dashboard", { params: f }).then((r) => setD(r.data)); }, [f]);
  if (!d) return <div className="text-[#5b6656]">Cargando…</div>;
  const s = d.sales, o = d.ots;

  return (
    <div data-testid="gerencia-dashboard-page">
      <PageHeader eyebrow="Gerencia" title="Dashboard gerencial" subtitle="Indicadores calculados desde los registros. Haga clic en un indicador para ver sus registros de origen." />
      <Filters f={f} setF={setF} lookups={lookups} ccs={ccs} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {s && <Kpi testid="kpi-pipeline" icon={TrendingUp} label="Pipeline (neto)" value={clp(s.pipeline_net)} to="/gerencia/ventas?status=cotizada,en_seguimiento" />}
        {s && <Kpi testid="kpi-awarded" icon={Award} label="Adjudicado (neto)" value={clp(s.awarded_net)} to="/gerencia/ventas?status=adjudicada" />}
        {s && <Kpi testid="kpi-winrate" icon={Target} label="Tasa de adjudicación" value={s.win_rate === null ? "—" : `${s.win_rate}%`} to="/gerencia/ventas?status=adjudicada" />}
        {s && <Kpi testid="kpi-awarded-no-ot" icon={AlertTriangle} label="Adjudicadas sin OT" value={s.awarded_without_ot} tone={s.awarded_without_ot ? "red" : ""} to="/gerencia/ventas?status=adjudicada" />}
        {o && <Kpi testid="kpi-ot-active" icon={Briefcase} label="OT activas" value={o.active} to="/gerencia/ot?status=PLANIFICADA,EN_EJECUCIÓN,SUSPENDIDA" />}
        {o && <Kpi testid="kpi-ot-overdue" icon={AlertTriangle} label="OT atrasadas" value={o.overdue} tone={o.overdue ? "red" : ""} to="/gerencia/ot?overdue=1" />}
        {o && d.can_see_economics && <Kpi testid="kpi-ot-awarded" icon={Award} label="Valor adjudicado OT" value={clp(o.awarded_total)} to="/gerencia/ot" />}
        {o && d.can_see_economics && <Kpi testid="kpi-ot-budget" icon={Target} label="Presupuesto OT" value={clp(o.budget_total)} to="/gerencia/ot" />}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        {s && (
          <div className="syc-card p-5" data-testid="chart-monthly">
            <div className="font-bold mb-3">Cotizado vs adjudicado por mes</div>
            {s.monthly.length === 0 ? <div className="text-sm text-[#5b6656] py-10 text-center">Sin datos en el período</div> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={s.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#eef0ec" /><XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1e6)}M`} />
                  <Tooltip formatter={(v) => clp(v)} /><Legend /><Bar dataKey="cotizado" fill="#5CD500" radius={[4, 4, 0, 0]} /><Bar dataKey="adjudicado" fill="#258F00" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
        {s && (
          <div className="syc-card p-5" data-testid="sales-by-status">
            <div className="font-bold mb-3">Ventas por estado</div>
            {Object.entries(s.by_status).map(([k, v]) => (
              <Link key={k} to={`/gerencia/ventas?status=${k}`} className="flex items-center justify-between py-2 border-b border-[#f0f2ee] text-sm hover:text-[#258F00]">
                <span>{OPP_STATUS[k].label}</span><span className="tabular-nums"><b>{v.count}</b> · {clp(v.net)}</span>
              </Link>
            ))}
            {s.lost_reasons.length > 0 && <div className="mt-4 text-xs text-[#5b6656]">Motivos de no adjudicación: {s.lost_reasons.map((r) => `${r.name} (${r.count})`).join(" · ")}</div>}
          </div>
        )}
        {o && (
          <div className="syc-card p-5" data-testid="ots-by-status">
            <div className="font-bold mb-3">OT por estado</div>
            {o.by_status.map((x) => (
              <Link key={x.code} to={`/gerencia/ot?status=${x.code}`} className="flex items-center justify-between py-2 border-b border-[#f0f2ee] text-sm hover:text-[#258F00]">
                <span>{x.name}</span><span className="tabular-nums"><b>{x.count}</b>{x.awarded !== null ? ` · ${clp(x.awarded)}` : ""}</span>
              </Link>
            ))}
          </div>
        )}
        {o && (
          <div className="syc-card p-5" data-testid="top-clients">
            <div className="font-bold mb-3">Principales clientes (OT)</div>
            {o.top_clients.length === 0 ? <div className="text-sm text-[#5b6656]">Sin OT registradas</div> : o.top_clients.map((c) => (
              <Link key={c.client_id} to={`/gerencia/ot?client_id=${c.client_id}`} className="flex items-center justify-between py-2 border-b border-[#f0f2ee] text-sm hover:text-[#258F00]">
                <span className="truncate">{c.name}</span><span className="tabular-nums"><b>{c.count}</b> OT{c.awarded !== null ? ` · ${clp(c.awarded)}` : ""}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-[#5b6656] mt-6" data-testid="costs-note">{d.costs_note}</p>
    </div>
  );
}
